// The Industry intelligence page's client (milestone 6 checks 3 and 4, track industry).
//
// MIRROR OF apps/api/src/industry/types.ts, field for field. It is a copy and not an import
// because the two packages do not depend on one another; `apps/api/src/industry/mirror.test.ts`
// reads this file and fails when a field exists on one side and not the other. Milestone 4 was
// bitten by a portal mock answering a shape the API did not and the live page went blank for
// Mike (cobrain-fan-out-lessons-2026-09-16), so the mirror is checked rather than trusted.
import { viewFetch as fetch } from '../view-as.js';
import { ApiError, Unauthenticated } from '../api.js';
import { dayMonth } from '../day.js';

export const WEIGHT_CLASSES = [
  'regulator_revenue_market', 'held_right', 'client_with_revenue', 'open_deal', 'declined_deal_with_reason',
  'prospect_with_open_deal', 'cold_prospect', 'product_line', 'territory',
] as const;
export type WeightClass = (typeof WEIGHT_CLASSES)[number];
export type TargetKind = 'organisation' | 'rights_property' | 'territory' | 'product_line';

export interface Target {
  kind: TargetKind;
  id: string | null;
  name: string;
  note: string;
  weightClass: WeightClass;
  territoryIso: string | null;
  forMe: boolean;
  counterpartyKey: string | null;
}

export interface DeclinedDeal {
  dealId: string;
  matterId: string;
  organisationId: string;
  organisationName: string;
  rightsPropertyName: string | null;
  lifecycle: 'declined' | 'paused';
  reason: string;
  at: string;
  inferred: boolean;
  confidence: number | null;
  pausedUntil: string | null;
}

export interface Implication {
  text: string;
  by: 'master' | 'rule';
  targetName: string;
  weightClass: WeightClass;
}

export interface IndustryItem {
  newsItemId: string;
  signalId: string;
  title: string;
  publisher: string;
  feedSource: string;
  url: string | null;
  at: string;
  regulator: boolean;
  rank: number;
  weightClass: WeightClass;
  weightBars: number;
  implication: string;
  implicationBy: 'master' | 'rule';
  implications: Implication[];
  targets: Target[];
  forMe: boolean;
  forMeBecause: string | null;
  declined: DeclinedDeal | null;
  owner: { principalId: string; name: string; unowned: boolean } | null;
  taken: Array<{ action: 'take' | 'raise'; byPrincipalId: string; byName: string; toPrincipalId: string | null; toName: string | null; at: string }>;
  removedForEveryone: { id: string; byPrincipalId: string; byName: string; at: string; undoableUntil: string } | null;
}

export interface RailEntry { key: string; label: string; note: string; count: number }
export interface Rail {
  territories: RailEntry[];
  rights: RailEntry[];
  products: RailEntry[];
  counterparties: RailEntry[];
  sources: RailEntry[];
  territoriesTotal: number;
}
export interface IndustryFilters {
  territory: string | null;
  right: string | null;
  product: string | null;
  counterparty: string | null;
  source: string | null;
  forMe: boolean;
}
export interface SourceLine { publisher: string; feedSource: string; cadence: string; lastAt: string | null }

export interface IndustryView {
  store: { tenantId: string; kind: string; name: string };
  items: IndustryItem[];
  digest: IndustryItem[];
  rail: Rail;
  sources: SourceLine[];
  newSinceLastLook: number;
  lastLookedAt: string | null;
  totalBeforeFilters: number;
  digestTotalBeforeFilters: number;
  windowDays: number;
  articlesRead: number;
  publishersRead: number;
  readingHasRun: boolean;
  matchSource: 'relevance_match' | 'news_match';
  isTrainer: boolean;
}

export interface RecordPanel {
  kind: TargetKind;
  id: string | null;
  title: string;
  rule: string;
  rows: Array<{ label: string; value: string }>;
  href: string | null;
  linkLabel: string | null;
  quote: string | null;
}
export interface WhyPanel {
  sentence: string;
  bullets: string[];
  adjustments: Array<{ key: 'higher' | 'lower' | 'wrong_target' | 'wrong_implication'; label: string }>;
  brain: { name: string; version: string | null; apprenticeAgreed: boolean | null } | null;
}
export interface NewsItemView {
  store: { tenantId: string; kind: string; name: string };
  item: IndustryItem;
  body: string | null;
  bodyIsEmpty: boolean;
  why: WhyPanel;
  panels: RecordPanel[];
  team: Array<{ principalId: string; name: string }>;
  isTrainer: boolean;
}

export type IndustryAction = 'raise' | 'take' | 'not_relevant' | 'park' | 'open' | 'read';
export interface DecideIndustryBody {
  store?: string;
  action: IndustryAction;
  forEveryone?: boolean;
  ownerPrincipalId?: string;
  until?: string;
  note?: string;
}
export interface DecideIndustryResult {
  store: { tenantId: string; kind: string; name: string };
  newsItemId: string;
  action: IndustryAction;
  decisionId: string | null;
  landedIn: { principalId: string; name: string; passItemId: string; passId: string; rankNo: number } | null;
  landingRefused: string | null;
  toldOwner: { principalId: string; name: string; notificationId: string } | null;
  removal: { id: string; scope: 'reader' | 'everyone'; undoableUntil: string } | null;
  parkId: string | null;
  openHref: string | null;
}

// ---- the calls -----------------------------------------------------------------------------

async function json<T>(res: Response): Promise<T> {
  if (res.status === 401) throw new Unauthenticated();
  if (!res.ok) {
    const said = await res.json()
      .then((b: unknown) => (typeof b === 'object' && b !== null && typeof (b as { error?: unknown }).error === 'string' ? (b as { error: string }).error : null))
      .catch(() => null);
    throw new ApiError(res.status, said ?? `${res.status} ${res.statusText}`);
  }
  return (await res.json()) as T;
}

export function queryOf(store: string, f: IndustryFilters): string {
  const q = new URLSearchParams({ store });
  if (f.territory) q.set('territory', f.territory);
  if (f.right) q.set('right', f.right);
  if (f.product) q.set('product', f.product);
  if (f.counterparty) q.set('counterparty', f.counterparty);
  if (f.source) q.set('source', f.source);
  if (f.forMe) q.set('forMe', '1');
  return q.toString();
}

export const EMPTY_FILTERS: IndustryFilters = { territory: null, right: null, product: null, counterparty: null, source: null, forMe: false };

export const industry = {
  list: (store: string, f: IndustryFilters): Promise<IndustryView> =>
    fetch(`/api/industry?${queryOf(store, f)}`, { credentials: 'same-origin' }).then((r) => json<IndustryView>(r)),
  archive: (store: string, q: { text?: string; from?: string; to?: string; touches?: string }): Promise<ArchiveView> => {
    const p = new URLSearchParams({ store });
    if (q.text) p.set('q', q.text);
    if (q.from) p.set('from', q.from);
    if (q.to) p.set('to', q.to);
    if (q.touches) p.set('touches', q.touches);
    return fetch(`/api/industry/archive?${p.toString()}`, { credentials: 'same-origin' }).then((r) => json<ArchiveView>(r));
  },
  item: (store: string, newsItemId: string): Promise<NewsItemView> =>
    fetch(`/api/industry/${encodeURIComponent(newsItemId)}?store=${encodeURIComponent(store)}`, { credentials: 'same-origin' }).then((r) => json<NewsItemView>(r)),
  decide: (newsItemId: string, body: DecideIndustryBody): Promise<DecideIndustryResult> =>
    fetch(`/api/industry/${encodeURIComponent(newsItemId)}/decide`, {
      method: 'POST', credentials: 'same-origin', headers: { 'content-type': 'application/json' }, body: JSON.stringify(body),
    }).then((r) => json<DecideIndustryResult>(r)),
  /** U62: a held Read sent as the page goes away (the tab closing, or leaving the page inside the app), as triage's decideOnLeave sends a held decision (U34). */
  decideOnLeave: (newsItemId: string, body: DecideIndustryBody): void => {
    void fetch(`/api/industry/${encodeURIComponent(newsItemId)}/decide`, {
      method: 'POST', credentials: 'same-origin', keepalive: true, headers: { 'content-type': 'application/json' }, body: JSON.stringify(body),
    }).catch(() => undefined);
  },
  undo: (newsItemId: string, store: string, removalId: string): Promise<{ undone: boolean }> =>
    fetch(`/api/industry/${encodeURIComponent(newsItemId)}/undo`, {
      method: 'POST', credentials: 'same-origin', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ store, removalId }),
    }).then((r) => json<{ undone: boolean }>(r)),
  adjust: (newsItemId: string, store: string, adjustment: WhyPanel['adjustments'][number]['key'], note?: string): Promise<{ judgementId: string | null }> =>
    fetch(`/api/industry/${encodeURIComponent(newsItemId)}/adjust`, {
      method: 'POST', credentials: 'same-origin', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ store, adjustment, note }),
    }).then((r) => json<{ judgementId: string | null }>(r)),
};

/** "today 07:35", "yesterday 16:20", "checked 1 Sep" — the artboard's own clock words. */
export function whenWords(iso: string, now: Date = new Date()): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const hm = `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  const day = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const diff = Math.round((day(now) - day(d)) / 86_400_000);
  if (diff === 0) return `today ${hm}`;
  if (diff === 1) return `yesterday ${hm}`;
  return dayMonth(d);
}

/** What the archive was asked for, echoed back so the page can say what it searched. */
export interface ArchiveQuery {
  text: string | null;
  from: string | null;
  to: string | null;
  touches: string | null;
  limit: number;
}

/** Everything ever read, searched. Nothing ages out of it. */
export interface ArchiveView {
  store: { tenantId: string; kind: string; name: string };
  asked: ArchiveQuery;
  items: IndustryItem[];
  /** How many answered, before the limit. */
  total: number;
  /** How many articles have ever been read, so an empty result can say what was searched. */
  readTotal: number;
  /** The oldest article held, so the page can say how far back it goes. */
  oldest: string | null;
}
